import { Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import ScrollToTopButton from "./components/ScrollToTopButton";
import { useMatomoPageView } from "./hooks/use-matomo";
import { TooltipProvider } from "@/components/ui/tooltip";

const Toaster = lazyWithRetry(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazyWithRetry(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

const AeoTechnology = lazyWithRetry(() => import("./pages/AeoTechnology"));
const AeoPlaybook = lazyWithRetry(() => import("./pages/AeoPlaybook"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const OpenAiActivation = lazyWithRetry(() => import("./pages/OpenAiActivation"));
const Services = lazyWithRetry(() => import("./pages/Services"));
const Resources = lazyWithRetry(() => import("./pages/Resources"));
const AgenticCommerceMarketWatch = lazyWithRetry(() => import("./pages/resources/AgenticCommerceMarketWatch"));
const AgenticCommerceReadiness = lazyWithRetry(() => import("./pages/resources/AgenticCommerceReadiness"));
const AgenticCommerceInMobility = lazyWithRetry(() => import("./pages/resources/AgenticCommerceInMobility"));
const AgenticCommerceBeyondAeo = lazyWithRetry(() => import("./pages/resources/AgenticCommerceBeyondAeo"));
const MeetUs = lazyWithRetry(() => import("./pages/MeetUs"));
const GrowthStrategy = lazyWithRetry(() => import("./pages/GrowthStrategy"));
const AeoToAgenticCommerce = lazyWithRetry(() => import("./pages/AeoToAgenticCommerce"));
const WhatIsAgenticCommerce = lazyWithRetry(() => import("./pages/answers/WhatIsAgenticCommerce"));
const AgenticCommerceProtocol = lazyWithRetry(() => import("./pages/answers/AgenticCommerceProtocol"));
const AgenticCommerceRisks = lazyWithRetry(() => import("./pages/answers/AgenticCommerceRisks"));
const AgenticCommerceOptimizationCompanies = lazyWithRetry(() => import("./pages/answers/AgenticCommerceOptimizationCompanies"));
const ArticleView = lazyWithRetry(() => import("./pages/resources/ArticleView"));
const AdminLogin = lazyWithRetry(() => import("./pages/admin/Login"));
const AdminResetPassword = lazyWithRetry(() => import("./pages/admin/ResetPassword"));
const AdminArticles = lazyWithRetry(() => import("./pages/admin/AdminArticles"));
const AdminArticleEditor = lazyWithRetry(() => import("./pages/admin/AdminArticleEditor"));
const GacLanding = lazyWithRetry(() => import("./pages/growth-canvas/Landing"));
const GacIntake = lazyWithRetry(() => import("./pages/growth-canvas/Intake"));
const GacGenerating = lazyWithRetry(() => import("./pages/growth-canvas/Generating"));
const GacResult = lazyWithRetry(() => import("./pages/growth-canvas/Result"));
const GacAdmin = lazyWithRetry(() => import("./pages/growth-canvas/Admin"));
const GacAdminReport = lazyWithRetry(() => import("./pages/growth-canvas/AdminCanvasReport"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));

function AppRoutes() {
  useMatomoPageView();
  return (
    <>
      <ScrollToTop />
      <ScrollToTopButton />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/services" element={<Services />} />
          <Route path="/resources" element={<Resources />} />
          <Route path="/resources/agentic-commerce-market-watch" element={<AgenticCommerceMarketWatch />} />
          <Route path="/resources/agentic-commerce-readiness" element={<AgenticCommerceReadiness />} />
          <Route path="/resources/agentic-commerce-in-mobility" element={<AgenticCommerceInMobility />} />
          <Route path="/resources/agentic-commerce-beyond-aeo" element={<AgenticCommerceBeyondAeo />} />
          <Route path="/aeo-technology" element={<AeoTechnology />} />
          <Route path="/aeo-playbook" element={<AeoPlaybook />} />
          <Route path="/openai-activation" element={<OpenAiActivation />} />
          <Route path="/meet-us" element={<MeetUs />} />
          <Route path="/growth-strategy" element={<GrowthStrategy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/aeo-to-agentic-commerce" element={<AeoToAgenticCommerce />} />
          <Route path="/what-is-agentic-commerce" element={<WhatIsAgenticCommerce />} />
          <Route path="/agentic-commerce-protocol" element={<AgenticCommerceProtocol />} />
          <Route path="/agentic-commerce-risks" element={<AgenticCommerceRisks />} />
          <Route path="/agentic-commerce-optimization-companies" element={<AgenticCommerceOptimizationCompanies />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin/reset-password" element={<AdminResetPassword />} />
          <Route path="/admin/articles" element={<AdminArticles />} />
          <Route path="/admin/articles/new" element={<AdminArticleEditor />} />
          <Route path="/admin/articles/:id" element={<AdminArticleEditor />} />
          <Route path="/your-growth-canvas" element={<GacLanding />} />
          <Route path="/your-growth-canvas/start" element={<GacIntake />} />
          <Route path="/your-growth-canvas/generating/:id" element={<GacGenerating />} />
          <Route path="/your-growth-canvas/result/:id" element={<GacResult />} />
          <Route path="/admin/growth-canvas" element={<GacAdmin />} />
          <Route path="/admin/growth-canvas/:id" element={<GacAdminReport />} />
          {/* Dynamic DB-backed article route — must come after static /resources/* routes */}
          <Route path="/resources/:slug" element={<ArticleView />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}

const App = () => (
  <TooltipProvider>
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </TooltipProvider>
);

export default App;
